import { Box, Button, Paper, Text, useComputedColorScheme, useMantineTheme } from '@mantine/core';
import { IconBrain, IconChevronDown, IconChevronUp } from '@tabler/icons-react';
import { useState } from 'react';

interface ReasoningDisplayProps {
  reasoning: string;
  previewLength?: number; // Number of characters to show in preview
}

export function ReasoningDisplay({ reasoning, previewLength = 80 }: ReasoningDisplayProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const theme = useMantineTheme();
  const computedColorScheme = useComputedColorScheme('light');
  const isDark = computedColorScheme === 'dark';

  if (!reasoning) {
    return null;
  }

  const needsCollapse = reasoning.length > previewLength;
  const previewText = needsCollapse ? `${reasoning.slice(0, previewLength)}...` : reasoning;

  return (
    <Paper
      p="sm"
      mb="xs"
      style={{
        backgroundColor: isDark ? theme.colors.dark[7] : theme.colors.gray[0],
        border: `1px solid ${isDark ? theme.colors.dark[5] : theme.colors.gray[2]}`,
        borderRadius: '8px',
      }}
    >
      <Box style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
        <IconBrain
          size={16}
          style={{
            color: isDark ? theme.colors['brand-primary'][4] : theme.colors.yellow[6],
            marginTop: '2px',
            flexShrink: 0,
          }}
        />
        <Box style={{ flex: 1 }}>
          <Text
            size="sm"
            style={{
              color: isDark ? theme.colors['brand-primary'][4] : theme.colors.yellow[7],
              fontWeight: 500,
              marginBottom: '4px',
            }}
          >
            Reasoning
          </Text>

          <Text
            size="sm"
            style={{
              color: isDark ? theme.colors.gray[3] : theme.colors.gray[7],
              lineHeight: 1.4,
              whiteSpace: 'pre-wrap',
            }}
          >
            {isExpanded ? reasoning : previewText}
          </Text>

          {needsCollapse && (
            <Button
              variant="subtle"
              size="xs"
              leftSection={isExpanded ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />}
              onClick={() => setIsExpanded(!isExpanded)}
              style={{
                marginTop: '8px',
                padding: '4px 8px',
                height: 'auto',
                color: isDark ? theme.colors['brand-primary'][4] : theme.colors.yellow[6],
              }}
            >
              {isExpanded ? 'Hide reasoning' : 'Show full reasoning'}
            </Button>
          )}
        </Box>
      </Box>
    </Paper>
  );
}
